import { useEffect, useState } from "react"
import type { CombinedError } from "urql"
import { useQuery } from "urql"

import type { Product, ProductQueryListArgs, Query } from "@/graphql/codegen/graphql"
import { GetTestTemplateProductListDocument } from "@/graphql/codegen/graphql"

interface UseGetTestTemplateProductsReturn {
  products: Product[]
  isFetching: boolean
  error: CombinedError | undefined
  loadMore: () => void
  refetch: () => void
}

const LIMIT = 100

export const useGetTestTemplateProducts = (testTemplateId?: string): UseGetTestTemplateProductsReturn => {
  const [products, setProducts] = useState<Product[]>([])
  const [offset, setOffset] = useState(0)
  const [hasNextPage, setHasNextPage] = useState(true)

  const [{ data, fetching, error }, reexecuteQuery] = useQuery<Pick<Query, "product">, ProductQueryListArgs>({
    query: GetTestTemplateProductListDocument,
    variables: { input: { filter: { testTemplates: [testTemplateId ?? ""] }, limit: LIMIT, offset } },
    pause: !testTemplateId,
  })

  useEffect(() => {
    if (data?.product.list.__typename === "ProductListSuccess") {
      const paginatedProducts = data.product.list.products
      setProducts((prev: Product[]) => (offset === 0 ? paginatedProducts : [...prev, ...paginatedProducts]))
      if (paginatedProducts.length < LIMIT) {
        setHasNextPage(false)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const loadMore = () => {
    if (hasNextPage) {
      setOffset((prevOffset) => prevOffset + LIMIT)
    }
  }

  const reset = () => {
    setOffset(0)
    setProducts([])
    setHasNextPage(true)
  }

  const refetch = () => {
    reset()
    reexecuteQuery({ requestPolicy: "network-only" })
  }

  return {
    products,
    isFetching: fetching,
    error,
    loadMore,
    refetch,
  }
}
